import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Check, Clipboard, KeyRound, Lock, Play, ShieldAlert, XCircle } from 'lucide-react'
import {
  AGENT_API_KEY_PRESETS,
  getMcpToolAccessSummary,
  type AgentPreset,
  type AgentPresetId,
  type FeatureProfile,
  type McpToolAccess,
} from '@agentwiki/shared'
import { Button, Card, Chip } from '../components/hybrid'
import { useAppStore } from '../stores/app-store'
import { apiClient } from '../lib/api-client'
import { cn } from '../lib/utils'
import { getAgentEndpoints } from '../lib/agent-endpoints'
import { buildMcpConfigSnippets } from '../lib/mcp-config-snippets'
import { runMcpConnectionTest, type McpConnectionTestResult } from '../lib/mcp-connection-test'
import { getWebFeatureProfile } from '../lib/feature-flags'

interface ApiKeySummary {
  id: string
  name: string
  keyPrefix: string
  scopes: string[]
  lastUsedAt: string | null
  expiresAt: string | null
  createdAt: string
}

interface CreatedKey {
  id: string
  key: string
  keyPrefix: string
  name: string
  scopes: string[]
}

const RISK_TONE = {
  low: 'ok',
  medium: 'warn',
  high: 'dgr',
} as const

function formatDate(value: string | null) {
  if (!value) return 'Never'
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(new Date(value))
}

function visibleTools(matrix: McpToolAccess[]) {
  return [
    ...matrix.filter((item) => item.available).slice(0, 7),
    ...matrix.filter((item) => !item.available).slice(0, 7),
  ]
}

function scopeList(scopes: readonly string[]) {
  return scopes.map((scope) => (
    <code key={scope} className="aw-agent-scope">
      {scope}
    </code>
  ))
}

const BASIC_SECRET_SCOPES = new Set(['secret:read', 'secret:create', 'secret:update'])
const STORAGE_TOOL_NAMES = new Set(['upload_file', 'upload_list', 'search_images'])

function isScopeVisible(scope: string, profile: FeatureProfile): boolean {
  if (!profile.storageSpace && scope.startsWith('storage:')) return false
  if (!profile.secretsAdvanced && scope.startsWith('secret:')) {
    return BASIC_SECRET_SCOPES.has(scope)
  }
  return true
}

export function getProfiledAgentPresets(profile: FeatureProfile): AgentPreset[] {
  return AGENT_API_KEY_PRESETS
    .filter((preset) => profile.storageSpace || preset.id !== 'storage-worker')
    .map((preset) => ({
      ...preset,
      scopes: preset.scopes.filter((scope) => isScopeVisible(scope, profile)),
    }))
}

export function getProfiledMcpToolAccessSummary(
  scopes: readonly string[],
  profile: FeatureProfile,
) {
  const summary = getMcpToolAccessSummary(scopes)
  const visible = (item: McpToolAccess) => (
    profile.storageSpace || !STORAGE_TOOL_NAMES.has(item.tool.name)
  )
  return {
    available: summary.available.filter(visible),
    blocked: summary.blocked.filter(visible),
    ungrantable: summary.ungrantable.filter(visible),
  }
}

export function AgentsPage() {
  const profile = getWebFeatureProfile()
  const presets = getProfiledAgentPresets(profile)
  const { theme } = useAppStore()
  const isDark = theme === 'dark'
  const qc = useQueryClient()
  const endpoints = useMemo(() => getAgentEndpoints(), [])
  const [selectedPresetId, setSelectedPresetId] = useState<AgentPresetId>('read-only-agent')
  const [createdKey, setCreatedKey] = useState<CreatedKey | null>(null)
  const [pastedKey, setPastedKey] = useState('')
  const [selectedSnippet, setSelectedSnippet] = useState('claude-desktop')
  const [copied, setCopied] = useState<string | null>(null)
  const [testResult, setTestResult] = useState<McpConnectionTestResult | null>(null)

  const selectedPreset = presets.find((preset) => preset.id === selectedPresetId) ?? presets[0]
  const usableSecret = createdKey?.key || pastedKey.trim()
  const snippets = buildMcpConfigSnippets({ mcpEndpoint: endpoints.mcp, apiKey: usableSecret || '<YOUR_API_KEY>' })
  const activeSnippet = snippets.find((snippet) => snippet.id === selectedSnippet) ?? snippets[0]
  const selectedSummary = getProfiledMcpToolAccessSummary(selectedPreset.scopes, profile)

  const { data: keysData, isLoading } = useQuery({
    queryKey: ['api-keys'],
    queryFn: () => apiClient.get<{ keys: ApiKeySummary[] }>('/api/keys'),
  })

  const createPresetKey = useMutation({
    mutationFn: (preset: AgentPreset) =>
      apiClient.post<CreatedKey>('/api/keys', {
        name: preset.label,
        scopes: [...preset.scopes],
      }),
    onSuccess: (result) => {
      setCreatedKey(result)
      setTestResult(null)
      qc.invalidateQueries({ queryKey: ['api-keys'] })
    },
  })

  const runTest = async () => {
    if (!usableSecret) return
    setTestResult(null)
    const result = await runMcpConnectionTest({
      endpoint: endpoints.mcp,
      apiKey: usableSecret,
      preset: selectedPreset,
    })
    setTestResult(result)
  }

  const copyText = async (id: string, text: string) => {
    if (typeof navigator === 'undefined') return
    await navigator.clipboard.writeText(text)
    setCopied(id)
    window.setTimeout(() => setCopied(null), 1600)
  }

  return (
    <div className="aw-agents-page">
      <section className="aw-page">
        <header className="aw-page-head">
          <h1 className="aw-home-hero">Agents / MCP</h1>
          <div className="aw-home-sub">
            Scoped keys · MCP snippets · live probe · tool matrix
          </div>
        </header>

        <div className="aw-page-body">
          <div className="aw-agent-grid">
            <section className="aw-agent-main">
              <div className="aw-agent-preset-grid">
                {presets.map((preset) => (
                  <button
                    key={preset.id}
                    type="button"
                    onClick={() => { setSelectedPresetId(preset.id); setTestResult(null) }}
                    className={cn('aw-agent-preset', selectedPreset.id === preset.id && 'active')}
                  >
                    <span className="aw-agent-preset-top">
                      <span>{preset.label}</span>
                      <Chip tone={RISK_TONE[preset.risk]}>{preset.risk}</Chip>
                    </span>
                    <span className="aw-agent-preset-desc">{preset.description}</span>
                    <span className="aw-agent-preset-count">{preset.scopes.length} scopes</span>
                  </button>
                ))}
              </div>

              <Card className="aw-agent-panel">
                <div className="aw-agent-panel-head">
                  <div>
                    <span className="aw-label">Selected preset</span>
                    <h2>{selectedPreset.label}</h2>
                  </div>
                  <Button type="button" variant="agent" onClick={() => createPresetKey.mutate(selectedPreset)} disabled={createPresetKey.isPending}>
                    <KeyRound className="h-4 w-4" />
                    {createPresetKey.isPending ? 'Creating...' : 'Create preset key'}
                  </Button>
                </div>
                <p className="aw-agent-copy">{selectedPreset.description}</p>
                <div className="aw-agent-scope-list">{scopeList(selectedPreset.scopes)}</div>

                {createdKey && (
                  <div className="aw-agent-secret">
                    <div className="aw-agent-secret-top">
                      <ShieldAlert className="h-4 w-4" />
                      <strong>Save now. This secret appears once.</strong>
                    </div>
                    <div className="aw-agent-secret-row">
                      <code>{createdKey.key}</code>
                      <button type="button" title="Copy key" onClick={() => copyText('secret', createdKey.key)}>
                        {copied === 'secret' ? <Check /> : <Clipboard />}
                      </button>
                    </div>
                    <button type="button" className="aw-agent-muted-button" onClick={() => setCreatedKey(null)}>
                      Dismiss secret
                    </button>
                  </div>
                )}
              </Card>

              <Card className="aw-agent-panel">
                <div className="aw-agent-panel-head">
                  <div>
                    <span className="aw-label">MCP config</span>
                    <h2>{activeSnippet.label}</h2>
                  </div>
                  <Chip tone="agent">{endpoints.mcp}</Chip>
                </div>
                <div className="aw-agent-tabs">
                  {snippets.map((snippet) => (
                    <button
                      key={snippet.id}
                      type="button"
                      className={cn(selectedSnippet === snippet.id && 'active')}
                      onClick={() => setSelectedSnippet(snippet.id)}
                    >
                      {snippet.label}
                    </button>
                  ))}
                </div>
                <div className="aw-agent-code-wrap">
                  <pre>{activeSnippet.code}</pre>
                  <button type="button" title="Copy snippet" onClick={() => copyText('snippet', activeSnippet.code)}>
                    {copied === 'snippet' ? <Check /> : <Clipboard />}
                  </button>
                </div>
              </Card>

              <Card className="aw-agent-panel">
                <div className="aw-agent-panel-head">
                  <div>
                    <span className="aw-label">Connection test</span>
                    <h2>MCP initialize + tools/list + probes</h2>
                  </div>
                  <Button type="button" variant="primary" onClick={runTest} disabled={!usableSecret}>
                    <Play className="h-4 w-4" />
                    Test MCP
                  </Button>
                </div>
                <input
                  type="password"
                  value={pastedKey}
                  onChange={(event) => { setPastedKey(event.target.value); setTestResult(null) }}
                  placeholder={createdKey ? 'Using newly created key' : 'Paste an API key secret'}
                  className={cn('aw-agent-input', isDark && 'dark')}
                />
                {testResult && (
                  <div className={cn('aw-agent-test-result', testResult.ok ? 'ok' : 'fail')}>
                    <div className="aw-agent-test-top">
                      {testResult.ok ? <Check className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
                      <strong>{testResult.ok ? 'Connection ready' : 'Connection blocked'}</strong>
                      <span>{testResult.toolCount} tools listed</span>
                    </div>
                    <ProbeRow title="Allowed probe" probe={testResult.allowedProbe} />
                    <ProbeRow title="Denied probe" probe={testResult.deniedProbe} />
                  </div>
                )}
              </Card>
            </section>

            <aside className="aw-agent-aside">
              <Card className="aw-agent-panel">
                <span className="aw-label">Preset tool access</span>
                <div className="aw-agent-metric-row">
                  <div><strong>{selectedSummary.available.length}</strong><span>available</span></div>
                  <div><strong>{selectedSummary.blocked.length}</strong><span>blocked</span></div>
                  <div><strong>{selectedSummary.ungrantable.length}</strong><span>ungrantable</span></div>
                </div>
                <ToolMatrix matrix={visibleTools([...selectedSummary.available, ...selectedSummary.blocked])} />
              </Card>

              <Card className="aw-agent-panel">
                <span className="aw-label">Saved keys</span>
                {isLoading ? (
                  <p className="aw-agent-copy">Loading keys...</p>
                ) : (keysData?.keys.length ?? 0) === 0 ? (
                  <p className="aw-agent-copy">No saved keys yet.</p>
                ) : (
                  <div className="aw-agent-key-list">
                    {keysData?.keys.map((key) => {
                      const summary = getProfiledMcpToolAccessSummary(key.scopes, profile)
                      return (
                        <div key={key.id} className="aw-agent-key-row">
                          <div>
                            <strong>{key.name}</strong>
                            <span>{key.keyPrefix} · created {formatDate(key.createdAt)}</span>
                          </div>
                          <div className="aw-agent-key-stats">
                            <Chip tone="ok">{summary.available.length} available</Chip>
                            <Chip tone="mut">{summary.blocked.length} blocked</Chip>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </Card>

              <Card className="aw-agent-panel">
                <span className="aw-label">Bridge</span>
                <p className="aw-agent-copy">
                  Settings API Keys still supports custom scope editing. This page is the preset and MCP test path.
                </p>
                <Button to="/settings?tab=api-keys">
                  <Lock className="h-4 w-4" />
                  Custom scopes
                </Button>
              </Card>
            </aside>
          </div>
        </div>
      </section>
    </div>
  )
}

function ProbeRow({ title, probe }: { title: string; probe: { toolName: string; ok: boolean; message: string } }) {
  return (
    <div className="aw-agent-probe-row">
      <span>{title}</span>
      <Chip tone={probe.ok ? 'ok' : 'dgr'}>{probe.toolName}</Chip>
      <p>{probe.message}</p>
    </div>
  )
}

function ToolMatrix({ matrix }: { matrix: McpToolAccess[] }) {
  return (
    <div className="aw-agent-tool-list">
      {matrix.map((item) => (
        <div key={item.tool.name} className={cn('aw-agent-tool-row', item.available && 'available')}>
          <div>
            <strong>{item.tool.label}</strong>
            <span>{item.tool.group} · {item.tool.requiredScope}</span>
          </div>
          <Chip tone={item.available ? 'ok' : item.grantable ? 'warn' : 'dgr'}>
            {item.available ? 'ready' : item.grantable ? 'blocked' : 'admin'}
          </Chip>
        </div>
      ))}
    </div>
  )
}
